import { defineChatViewActions } from './actionsContext'
import {
  applyCoreLastMessageToChatOut,
} from './listHelpers'
import { useChatStore as useChatStoreCore } from '../chatStore'
import { useRateLimit } from '../../composables/useRateLimit'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import {
  ChatTransformer,
} from '../../services'
import {
  mergeEventChatFields,
} from '../../constants/chat'
import { summarizeTopicForLog, topicsDebugLog, debugConsoleError } from '../../utils/chatDebugLog'
import { isTopicsModeChat } from '@/modules/chat-matrix-fast/utils/topics'
import { resolveChatOnlineStatus } from '@/modules/chat-matrix-fast/utils/chatOnlineStatus'
import type { ChatOut } from '../../types'
import type { IChat, IMessage } from '@/modules/chat-matrix-fast/types/IChat'

export const syncActions = defineChatViewActions({
    initRateLimiters() {
      if (!this.messageRateLimiter) {
        this.messageRateLimiter = useRateLimit({ maxRequests: 5, windowMs: 1000 })
      }

      if (!this.fileMessageRateLimiter) {
        this.fileMessageRateLimiter = useRateLimit({ maxRequests: 3, windowMs: 1000 })
      }
    },


    showNotification(message: string, type: 'success' | 'info' | 'warning' | 'error' = 'error'): void {
      const feedbackStore = useFeedbackStore()

      feedbackStore.showToast({
        type,
        message,
        time: 3000,
      })
    },

    removeChatFromUi(chatId: number | string): void {
      if (this.activeChat?.chat?.id === chatId) {
        this.activeChat = null
      }

      this.chatsContacts = this.chatsContacts.filter(
        contact => contact.chat?.id !== chatId && contact.id !== chatId,
      )
    },

    syncActiveChatOnline(): void {
      const activeChatId = this.activeChat?.chat?.id
      if (!activeChatId || !this.activeChat) {
        return
      }

      const chatStore = useChatStoreCore()
      const coreChat = chatStore.chats.find(chat => chat.id === activeChatId)
      if (!coreChat) {
        return
      }

      const online = resolveChatOnlineStatus(coreChat)
      if (this.activeChat.contact.online === online) {
        return
      }

      this.activeChat = {
        ...this.activeChat,
        contact: {
          ...this.activeChat.contact,
          online,
        },
      }
    },

    syncWithCoreStore() {
      const chatStore = useChatStoreCore()

      // В режиме поиска не перезаписываем списки — иначе пропадают «Контакты»
      if (chatStore.searchResults !== null) {
        return
      }

      if (this.activeChat?.chat) {
        try {
          const currentChatId = this.activeChat.chat.id

          // 1. Из chatStore.chats (Matrix timeline / API)
          const lastMessageFromCoreStore = chatStore.chats.find(c => c.id === currentChatId)?.last_message

          const originalLastMessage
            = lastMessageFromCoreStore
            || chatStore.currentChat?.last_message
            || (this.activeChat.chat as any).lastMessage

          const messagesToUse: IMessage[] = []

          if (chatStore.activeTopic || isTopicsModeChat(chatStore.currentChat)) {
            topicsDebugLog('view · syncWithCoreStore · activeChat metadata (no message mirror)', {
              currentChatId,
              coreCurrentChatId: chatStore.currentChat?.id ?? null,
              coreMessagesCount: chatStore.messages.length,
              activeTopic: summarizeTopicForLog(chatStore.activeTopic),
            })
          }

          // Merge from core-store, not UI-only activeChat — keeps total_members etc.
          const activeChatInStore = chatStore.chats.find(c => c.id === currentChatId)
          const previousActiveChat = this.activeChat.chat as IChat
          const currentChatFallback = chatStore.currentChat?.id === currentChatId
            ? chatStore.currentChat
            : undefined
          const baseChatForTransform = mergeEventChatFields(
            (activeChatInStore ?? previousActiveChat) as IChat,
            previousActiveChat,
            currentChatFallback,
          )
          const chatToTransform = originalLastMessage
            ? { ...baseChatForTransform, last_message: originalLastMessage }
            : baseChatForTransform

          const transformedChat = mergeEventChatFields(
            ChatTransformer.transformChatToChatOut(
              chatToTransform,
              messagesToUse,
            ) as IChat,
            chatToTransform,
            previousActiveChat,
            currentChatFallback,
          ) as ChatOut

          if (activeChatInStore?.last_message) {
            applyCoreLastMessageToChatOut(transformedChat, activeChatInStore.last_message)
          } else if (chatStore.messages.length > 0 && chatStore.currentChat?.id === currentChatId) {
            applyCoreLastMessageToChatOut(
              transformedChat,
              chatStore.messages[chatStore.messages.length - 1],
            )
          }

          // Не тащим timeline в activeChat — UI читает core через useActiveChatMessages.
          transformedChat.messages = []

          this.activeChat.chat = transformedChat

          if (!this.activeChat.chat || this.activeChat.chat.id !== currentChatId) {
          }
        } catch (error) {
          debugConsoleError('[chatViewStore] Ошибка в syncWithCoreStore при обновлении activeChat:', error)
        }
      }

      const activeChatId = this.activeChat?.chat?.id

      if (!chatStore.chats.length) {
        return
      }

      this.rebuildChatsContactsFromCore()

      if (activeChatId && this.activeChat?.chat?.id === activeChatId) {
        const updatedActiveContact = this.chatsContacts.find(c => c.chat?.id === activeChatId)
        if (updatedActiveContact) {
          this.activeChat.contact = updatedActiveContact
        } else {
          this.activeChat = null
        }
      }
    },

    forceSortChats(): void {
      this.rebuildChatsContactsFromCore({ resort: true })
    },

    updateContactLastMessage(chatId: number | string, _message: IMessage): void {
      this.rebuildChatsContactsFromCore({ bumpChatId: chatId })
    },

    moveChatToTop(chatId: number | string): void {
      this.rebuildChatsContactsFromCore({ bumpChatId: chatId })
    },
})
